/**
 * Which notes are still legal, why a measure has run out of them, and whether
 * the exercise can still be finished at all.
 *
 * The trick used throughout: place a candidate hypothetically, re-analyse, and
 * keep it only if it introduced no *new* hard finding relative to the same
 * position with that measure empty. That way a candidate is judged in full
 * context — against what stands before it and after it — without duplicating a
 * single rule from `analysis.ts`.
 */

import type { Candidate, Exercise, RulesProfile } from './types';
import { measureCount } from './types';
import { analyze, findingKey, isHard } from './analysis';
import {
  diatonicDistance,
  edoOf,
  nameOfPitch,
  naturalPitchOf,
  staffPositionOf,
  tuningStepsBetween,
} from './pitch';
import { isConsonant } from './intervals';

/** The writable staff-position range on the student's side of the cantus. */
function staffWindow(ex: Exercise, measure: number): { lo: number; hi: number } {
  const cf = staffPositionOf(ex.cf[measure], edoOf(ex));
  return ex.cfPos === 'lower' ? { lo: cf, hi: cf + 11 } : { lo: cf - 11, hi: cf };
}

/**
 * Every pitch in chromatic scale order inside a measure's writable range.
 * 12-EDO retains one key-derived pitch per staff position. 31-EDO traverses all
 * 31 canonically spelled pitch classes, including double flats and sharps.
 */
export function pitchesInWindow(ex: Exercise, measure: number): number[] {
  const { lo, hi } = staffWindow(ex, measure);
  if (edoOf(ex) === 12) return Array.from({ length: hi - lo + 1 }, (_, i) => lo + i);

  const first = naturalPitchOf(lo, 31) - 4;
  const last = naturalPitchOf(hi, 31) + 4;
  const pitches: number[] = [];
  for (let pitch = first; pitch <= last; pitch++) {
    const d = staffPositionOf(pitch, 31);
    if (d >= lo && d <= hi) pitches.push(pitch);
  }
  return pitches;
}

/**
 * The pitch window a counterpoint note may occupy: a twelfth on the far side of
 * the cantus firmus. Wider than any rule allows, so nothing legal is ever hidden
 * off the staff — deep ledger lines are drawn instead.
 */
export function pitchWindow(ex: Exercise, measure: number): { lo: number; hi: number } {
  const pitches = pitchesInWindow(ex, measure);
  return { lo: pitches[0], hi: pitches[pitches.length - 1] };
}

/** Clamp a pitch into the writable window for a measure. */
export function clampToWindow(ex: Exercise, measure: number, d: number): number {
  const pitches = pitchesInWindow(ex, measure);
  return pitches.reduce((best, pitch) => Math.abs(pitch - d) < Math.abs(best - d) ? pitch : best, pitches[0]);
}

/** Move through the tuning's scale order, clamped to the writable range. */
export function adjacentPitch(ex: Exercise, measure: number, current: number, delta: number): number {
  const pitches = pitchesInWindow(ex, measure);
  const exact = pitches.indexOf(current);
  const start = exact >= 0
    ? exact
    : pitches.reduce((best, pitch, i) => Math.abs(pitch - current) < Math.abs(pitches[best] - current) ? i : best, 0);
  return pitches[Math.min(Math.max(start + delta, 0), pitches.length - 1)];
}

/** Replace one measure of the counterpoint, returning a new exercise. */
export function withNote(ex: Exercise, measure: number, d: number | null): Exercise {
  const cp = ex.cp.slice();
  cp[measure] = d;
  return { ...ex, cp };
}

/**
 * Every pitch that may legally be written in `measure` right now.
 *
 * A measure that already holds a note still gets a set — the note is lifted for
 * the test — so the UI can keep showing the alternatives it was chosen from.
 */
export function legalSet(ex: Exercise, measure: number, profile: RulesProfile): Candidate[] {
  const n = measureCount(ex);
  const empty = withNote(ex, measure, null);
  const baseline = new Set(analyze(empty, profile).findings.filter(isHard).map(findingKey));

  const out: Candidate[] = [];
  for (const d of pitchesInWindow(ex, measure)) {
    const trial = withNote(empty, measure, d);
    const introduced = analyze(trial, profile).findings.some((f) => isHard(f) && !baseline.has(findingKey(f)));
    if (!introduced) out.push({ d, formula: measure === 0 || measure === n - 2 || measure === n - 1 });
  }
  return out;
}

/**
 * Why is this measure a dead end? For each *consonant* candidate — the ones a
 * student would reach for — the first rule that blocks it.
 */
export function explainDeadEnd(ex: Exercise, measure: number, profile: RulesProfile): string[] {
  const cf = ex.cf[measure];
  const empty = withNote(ex, measure, null);
  const baseline = new Set(analyze(empty, profile).findings.filter(isHard).map(findingKey));

  const out: string[] = [];
  for (const d of pitchesInWindow(ex, measure)) {
    const dd = Math.abs(diatonicDistance(d, cf, ex));
    const steps = Math.abs(tuningStepsBetween(d, cf, measure, ex));
    if (!isConsonant(dd, steps, edoOf(ex))) continue;
    const trial = withNote(empty, measure, d);
    const blocker = analyze(trial, profile).findings.find((f) => isHard(f) && !baseline.has(findingKey(f)));
    if (blocker) out.push(`${nameOfPitch(d, edoOf(ex))} — ${blocker.message}`);
  }
  return out;
}

/**
 * Depth-first search: can the exercise still be completed legally from here?
 *
 * Used two ways — to warn the student that they have written themselves into a
 * corner, and to vet a freshly generated cantus firmus before it is ever shown.
 */
export function isSolvable(ex: Exercise, profile: RulesProfile): boolean {
  const n = measureCount(ex);
  const cp = ex.cp.slice();
  const open: number[] = [];
  for (let m = 0; m < n; m++) if (cp[m] == null) open.push(m);

  const step = (k: number): boolean => {
    if (k === open.length) return true;
    const m = open[k];
    for (const c of legalSet({ ...ex, cp }, m, profile)) {
      cp[m] = c.d;
      if (step(k + 1)) return true;
      cp[m] = null;
    }
    return false;
  };
  return step(0);
}

/** The derived legality picture for a whole position, recomputed on every edit. */
export interface Legality {
  /** One candidate list per measure. */
  readonly allowed: readonly Candidate[][];
  /** For each empty measure with no candidates, why each consonance is blocked. */
  readonly deadEnds: Readonly<Record<number, string[]>>;
  /** True when no legal completion exists at all, even though measures still show options. */
  readonly positionDead: boolean;
}

/**
 * Compute everything the allowed-notes assist needs.
 *
 * `withLookahead` is false when the assist is off: the candidate lists are still
 * wanted (the keyboard cursor snaps to them, and grading never sees them), but
 * the expensive dead-end search is part of the assist, not the grader.
 */
export function computeLegality(ex: Exercise, profile: RulesProfile, withLookahead: boolean): Legality {
  const n = measureCount(ex);
  const allowed = ex.cf.map((_, m) => legalSet(ex, m, profile));
  if (!withLookahead) return { allowed, deadEnds: {}, positionDead: false };

  const deadEnds: Record<number, string[]> = {};
  for (let m = 0; m < n; m++) {
    if (ex.cp[m] == null && allowed[m].length === 0) deadEnds[m] = explainDeadEnd(ex, m, profile);
  }
  const positionDead = ex.cp.some((d) => d == null) && !isSolvable(ex, profile);
  return { allowed, deadEnds, positionDead };
}
